import React, { useState, useEffect, useMemo } from 'react';
import { getBookingsByDateAndBus, getBusSeatAvailability } from '../../Services/Operations/ProductAPI';

const getTodayStr = () => new Date().toISOString().split('T')[0];

const getInitials = (fullName = '') => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const ACTIVE_STATUSES = ['confirmed', 'in-progress', 'inprogress'];

const parseSeatConfig = (busType) => {
  if (!busType || typeof busType !== 'string') return { left: 2, right: 2 };
  const match = busType.match(/(\d+)\s*\*\s*(\d+)/);
  if (!match) return { left: 2, right: 2 };
  return { left: parseInt(match[1], 10), right: parseInt(match[2], 10) };
};

const SeatTooltip = ({ children, seat, bookedSeatsMap, blockedSeatsMap, busOwner }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  const seatNumber = seat.seatNumber;
  const isBooked = !!bookedSeatsMap[seatNumber];
  const isBlocked = seat.isBlocked === true || blockedSeatsMap[seatNumber] === true;
  
  const seatData = seat._apiData || {};
  const passengerDetails = seatData.passengerDetails || null;
  const ownerDetails = seatData.ownerDetails || null;
  
  const bookingInfo = bookedSeatsMap[seatNumber];
  
  const getPnr = () => {
    if (passengerDetails?.pnr) return passengerDetails.pnr;
    if (bookingInfo?.pnr) return bookingInfo.pnr;
    if (seatData?.pnr) return seatData.pnr;
    if (bookingInfo?.passengerDetails?.pnr) return bookingInfo.passengerDetails.pnr;
    return null;
  };
  
  const getPassengerName = () => {
    if (passengerDetails?.name) return passengerDetails.name;
    if (passengerDetails?.passengerName) return passengerDetails.passengerName;
    if (bookingInfo?.passengerName) return bookingInfo.passengerName;
    if (bookingInfo?.passengerDetails?.name) return bookingInfo.passengerDetails.name;
    if (bookingInfo?.passengerDetails?.passengerName) return bookingInfo.passengerDetails.passengerName;
    return null;
  };
  
  const getTooltipContent = () => {
    if (isBlocked) {
      const owner = ownerDetails || busOwner;
      return (
        <div className="seat-tooltip-content blocked-tooltip">
          <h4>🚫 Blocked Seat</h4>
          <p><strong>Seat:</strong> {seatNumber}</p>
          {owner && (
            <>
              <p><strong>Owner:</strong> {owner.ownerName || owner.name || 'N/A'}</p>
              <p><strong>Contact:</strong> {owner.ownerPhone || owner.phone || 'N/A'}</p>
              {owner.companyName && <p><strong>Company:</strong> {owner.companyName}</p>}
            </>
          )}
        </div>
      );
    }
    
    if (isBooked) {
      const pnr = getPnr();
      const passengerName = getPassengerName();
      const info = passengerDetails || bookingInfo?.passengerDetails || bookingInfo;
      
      return (
        <div className="seat-tooltip-content booked-tooltip">
          <h4>🎫 Booked Seat</h4>
          <p><strong>Seat:</strong> {seatNumber}</p>
          {pnr && (
            <p className="pnr-highlight"><strong>🎟️ PNR:</strong> <span className="pnr-value">{pnr}</span></p>
          )}
          <p><strong>Name:</strong> {passengerName || info?.name || info?.passengerName || 'N/A'}</p>
          {info?.age && <p><strong>Age:</strong> {info.age}</p>}
          {info?.gender && <p><strong>Gender:</strong> {info.gender}</p>}
          {info?.mobile && <p><strong>Contact:</strong> {info.mobile}</p>}
          {info?.email && <p><strong>Email:</strong> {info.email}</p>}
          {(info?.from || info?.to) && (
            <p><strong>Route:</strong> {info.from || 'N/A'} → {info.to || 'N/A'}</p>
          )}
          {(info?.boardingPoint || info?.droppingPoint) && (
            <>
              {info.boardingPoint && <p><strong>Boarding:</strong> {info.boardingPoint}</p>}
              {info.droppingPoint && <p><strong>Dropping:</strong> {info.droppingPoint}</p>}
            </>
          )}
          {info?.bookingType && <p><strong>Booking Type:</strong> {info.bookingType}</p>}
          {info?.fare && <p><strong>Fare:</strong> ₹{info.fare}</p>}
          {info?.totalAmount && <p><strong>Total:</strong> ₹{info.totalAmount}</p>}
        </div>
      );
    }
    
    return (
      <div className="seat-tooltip-content available-tooltip">
        <p><strong>Seat:</strong> {seatNumber}</p>
        <p><strong>Status:</strong> Available ✓</p>
      </div>
    );
  };
  
  return (
    <div 
      className="seat-tooltip-wrapper"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {children}
      {showTooltip && (
        <div className="seat-tooltip-popup">
          {getTooltipContent()}
        </div>
      )}
    </div>
  );
};

const LiveSeatStatusModal = ({ bus, token, onClose }) => {
  console.log("Bus ::::::::::::: ", bus);
  const [selectedDate, setSelectedDate] = useState(getTodayStr());
  const [selectedDirection, setSelectedDirection] = useState('UP');
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  
  const [seatData, setSeatData] = useState(null);
  const [bookedSeatsMap, setBookedSeatsMap] = useState({});
  const [blockedSeatsMap, setBlockedSeatsMap] = useState({});
  const [totalBookedSeats, setTotalBookedSeats] = useState(0);
  const [totalBlockedSeats, setTotalBlockedSeats] = useState(0);
  const [availableSeats, setAvailableSeats] = useState(bus.totalSeats || 0);
  const [instantCounts, setInstantCounts] = useState({ UP: 0, DOWN: 0, total: 0 });
  const [busOwner, setBusOwner] = useState(null);

  const extractTimeSlots = () => {
    const slots = [];
    
    console.log('Bus routeSchedules:', bus.routeSchedules);
    
    if (bus.routeSchedules && bus.routeSchedules.length > 0) {
      const routeSchedule = bus.routeSchedules.find(rs => rs.direction === selectedDirection);
      
      console.log('Route schedule for direction:', selectedDirection, routeSchedule);
      
      if (routeSchedule) {
        const originTimings = routeSchedule.origin?.timings || [];
        const destTimings = routeSchedule.destination?.timings || [];
        
        if (originTimings.length > 0) {
          originTimings.forEach(timing => {
            const slotNumber = timing.timeSlotSequence || 1;
            const departureTime = timing.departureTime;
            
            let arrivalTime = null;
            if (destTimings.length > 0) {
              const destTiming = destTimings.find(
                t => t.timeSlotSequence === slotNumber
              );
              if (destTiming) {
                arrivalTime = destTiming.arrivalTime;
              }
            }
            
            if (departureTime) {
              slots.push({
                slot: slotNumber,
                departureTime: departureTime,
                arrivalTime: arrivalTime || 'N/A',
                label: `Slot ${slotNumber} - ${departureTime} → ${arrivalTime || 'N/A'}`
              });
            }
          });
        }
        
        if (slots.length === 0 && destTimings.length > 0) {
          destTimings.forEach(timing => {
            const slotNumber = timing.timeSlotSequence || 1;
            const arrivalTime = timing.arrivalTime;
            
            let departureTime = null;
            if (originTimings.length > 0) {
              const originTiming = originTimings.find(
                t => t.timeSlotSequence === slotNumber
              );
              if (originTiming) {
                departureTime = originTiming.departureTime;
              }
            }
            
            if (arrivalTime) {
              slots.push({
                slot: slotNumber,
                departureTime: departureTime || 'N/A',
                arrivalTime: arrivalTime,
                label: `Slot ${slotNumber} - ${departureTime || 'N/A'} → ${arrivalTime}`
              });
            }
          });
        }
        
        if (slots.length === 0) {
          const departureTime = routeSchedule.origin?.departureTime || 'N/A';
          const arrivalTime = routeSchedule.destination?.arrivalTime || 'N/A';
          if (departureTime !== 'N/A' || arrivalTime !== 'N/A') {
            slots.push({
              slot: 1,
              departureTime: departureTime,
              arrivalTime: arrivalTime,
              label: `Slot 1 - ${departureTime} → ${arrivalTime}`
            });
          }
        }
      }
    }
    
    if (slots.length === 0) {
      slots.push({
        slot: 1,
        departureTime: 'N/A',
        arrivalTime: 'N/A',
        label: 'Slot 1'
      });
    }
    
    slots.sort((a, b) => a.slot - b.slot);
    console.log('Extracted time slots:', slots);
    setAvailableTimeSlots(slots);
    
    if (!slots.some(s => s.slot === selectedTimeSlot)) {
      setSelectedTimeSlot(slots[0]?.slot || 1);
    }
  };
const fetchSeatAvailability = async () => {
  try {
    setLoading(true);
    setError(null);

    console.log('📡 Fetching seat availability with:', {
      busId: bus.id,
      date: selectedDate,
      direction: selectedDirection,
      timeSlot: selectedTimeSlot
    });

    const response = await getBusSeatAvailability(
      token,
      bus.id,
      selectedDate,
      selectedDirection,
      selectedTimeSlot
    );

    console.log('📦 Seat availability response:', response);

    if (response && response.success) {
      setSeatData(response);
      
      if (response.bus && response.bus.owner) {
        setBusOwner(response.bus.owner);
      }
      
      const directionData = response.directions?.find(
        d => d.direction === selectedDirection
      );
      
      if (directionData) {
        const slotData = directionData.timeSlots?.find(
          s => s.timeSlot === selectedTimeSlot
        );
        
        if (slotData) {
          const bookedMap = {};
          const blockedMap = {};
          
          const seatDataMap = {};
          slotData.seats?.forEach(seat => {
            seatDataMap[seat.seatNumber] = seat;
            
            if (seat.isBooked && !seat.isCancelled) {
              const passengerDetails = seat.passengerDetails || {};
              bookedMap[seat.seatNumber] = {
                passengerName: seat.passengerName || passengerDetails?.name || 'Unknown',
                bookingId: seat.bookingId,
                pnr: seat.pnr || passengerDetails?.pnr || null,
                initials: seat.passengerName ? getInitials(seat.passengerName) : 'B',
                passengerDetails: {
                  name: passengerDetails?.name || seat.passengerName || 'Unknown',
                  age: passengerDetails?.age || null,
                  gender: passengerDetails?.gender || null,
                  mobile: passengerDetails?.mobile || null,
                  email: passengerDetails?.email || null,
                  from: passengerDetails?.from || null,
                  to: passengerDetails?.to || null,
                  boardingPoint: passengerDetails?.boardingPoint || null,
                  droppingPoint: passengerDetails?.droppingPoint || null,
                  fare: passengerDetails?.fare || null,
                  totalAmount: passengerDetails?.totalAmount || null,
                  bookingType: passengerDetails?.bookingType || seat.bookingType || 'customer',
                  pnr: seat.pnr || passengerDetails?.pnr || null
                }
              };
            }
            if (seat.isBlocked) {
              blockedMap[seat.seatNumber] = true;
            }
          });
          
          setBookedSeatsMap(bookedMap);
          setBlockedSeatsMap(blockedMap);
          setTotalBookedSeats(slotData.bookedSeats || 0);
          setTotalBlockedSeats(slotData.blockedSeats || 0);
          setAvailableSeats(slotData.availableSeats || 0);
          
          if (slotData.instantCounts) {
            setInstantCounts(slotData.instantCounts);
          }
        }
      }
    } else {
      setError(response?.message || 'Failed to fetch seat availability');
      await fetchBookingsFallback();
    }
  } catch (err) {
    console.error('Error fetching seat availability:', err);
    setError(err.message || 'Failed to load seat availability');
    await fetchBookingsFallback();
  } finally {
    setLoading(false);
  }
};

  const fetchBookingsFallback = async () => {
    try {
      console.log('🔄 Using fallback bookings API');
      const response = await getBookingsByDateAndBus(token, bus.id, selectedDate);
      if (response && response.success) {
        const bookings = response.data || [];
        processBookingsData(bookings);
      }
    } catch (err) {
      console.error('Error in fallback:', err);
      setError('Failed to load seat data. Please try again.');
    }
  };

  const processBookingsData = (bookings) => {
    const instant = { UP: 0, DOWN: 0, total: 0 };
    const bookedMap = {};
    const blockedMap = {};

    (bus.seats || []).forEach(seat => {
      if (seat.isBlocked === true) {
        blockedMap[seat.seatNumber] = true;
      }
    });

    bookings.forEach((booking) => {
      const direction = booking.journeyDetails?.direction === 'DOWN' ? 'DOWN' : 'UP';
      const timeSlot = booking.timeSlotSequence || 1;
      
      if (direction === selectedDirection && timeSlot === selectedTimeSlot) {
        const status = (booking.status || '').toLowerCase();
        const isActive = ACTIVE_STATUSES.includes(status);
        
        if (!isActive) return;

        if (booking.isInstantBooking) {
          instant[direction] += 1;
          instant.total += 1;
          return;
        }

        if (booking.seats) {
          booking.seats.forEach((seat) => {
            if (seat.seatNumber) {
              bookedMap[seat.seatNumber] = {
                passengerName: seat.passenger?.fullName || 'Unknown',
                bookingId: booking._id,
                pnr: booking.pnr || booking.maskedPnr,
                initials: seat.passenger?.fullName ? getInitials(seat.passenger.fullName) : 'B',
                passengerDetails: {
                  name: seat.passenger?.fullName || 'Unknown',
                  age: seat.passenger?.age || null,
                  gender: seat.passenger?.gender || null,
                  mobile: seat.passenger?.mobileNumber || null,
                  email: seat.passenger?.email || null,
                  from: booking.route?.from || null,
                  to: booking.route?.to || null,
                  bookingType: booking.isManual ? 'manual' : 'customer',
                  pnr: booking.pnr || booking.maskedPnr
                }
              };
            }
          });
        }
      }
    });

    setInstantCounts(instant);
    setBookedSeatsMap(bookedMap);
    setBlockedSeatsMap(blockedMap);
    setTotalBookedSeats(Object.keys(bookedMap).length);
    setTotalBlockedSeats(Object.keys(blockedMap).length);
    setAvailableSeats(bus.totalSeats - Object.keys(bookedMap).length - Object.keys(blockedMap).length);
  };

  useEffect(() => {
    extractTimeSlots();
  }, [selectedDirection]);

  useEffect(() => {
    if (bus.id && selectedDate) {
      fetchSeatAvailability();
    }
  }, [selectedDate, selectedDirection, selectedTimeSlot]);

  const { left: leftCount, right: rightCount } = useMemo(
    () => parseSeatConfig(bus.seatConfiguration?.busType),
    [bus.seatConfiguration]
  );

  const seatRows = useMemo(() => {
    const rowsMap = {};
    (bus.seats || []).forEach((seat) => {
      if (!rowsMap[seat.row]) rowsMap[seat.row] = [];
      rowsMap[seat.row].push(seat);
    });
    return Object.keys(rowsMap)
      .map(Number)
      .sort((a, b) => a - b)
      .map((rowNum) => rowsMap[rowNum].sort((a, b) => a.column - b.column));
  }, [bus.seats]);

  const renderSeatBox = (seat) => {
    const info = bookedSeatsMap[seat.seatNumber];
    const isBooked = !!info;
    const isBlocked = seat.isBlocked === true || blockedSeatsMap[seat.seatNumber] === true;
    
    const seatWithData = {
      ...seat,
      _apiData: seatData?.directions
        ?.find(d => d.direction === selectedDirection)
        ?.timeSlots
        ?.find(s => s.timeSlot === selectedTimeSlot)
        ?.seats
        ?.find(s => s.seatNumber === seat.seatNumber) || null
    };

    const getSeatClassName = () => {
      if (isBlocked) return 'seat-blocked';
      if (isBooked) return 'seat-booked';
      return 'seat-available';
    };

    const getSeatDisplayText = () => {
      if (isBlocked) return '🚫';
      if (isBooked) return info?.initials || 'B';
      return seat.seatNumber;
    };

    return (
      <SeatTooltip
        key={seat.seatNumber}
        seat={seatWithData}
        bookedSeatsMap={bookedSeatsMap}
        blockedSeatsMap={blockedSeatsMap}
        busOwner={busOwner}
      >
        <div
          className={`seat-box ${getSeatClassName()}`}
        >
          {getSeatDisplayText()}
        </div>
      </SeatTooltip>
    );
  };

  const renderSeatGrid = () => {
    if (seatRows.length === 0) {
      return <p style={{ fontSize: '13px', color: '#666' }}>No seat layout available for this bus.</p>;
    }

    return (
      <div className="seat-grid1">
        {seatRows.map((row, rowIdx) => {
          const isBackRow = row.every((s) => s.position === 'back');

          if (isBackRow) {
            return (
              <div className="seat-row seat-row-back" key={rowIdx}>
                {row.map((seat) => renderSeatBox(seat))}
              </div>
            );
          }

          const leftSeats = row.filter((s) => s.position === 'left' || s.column <= leftCount);
          const rightSeats = row.filter((s) => s.position === 'right' || s.column > leftCount);
          
          const finalLeftSeats = leftSeats.length > 0 ? leftSeats : row.slice(0, leftCount);
          const finalRightSeats = rightSeats.length > 0 ? rightSeats : row.slice(leftCount);

          return (
            <div className="seat-row" key={rowIdx}>
              <div className="seat-row-block seat-row-left">
                {finalLeftSeats.map((seat) => renderSeatBox(seat))}
              </div>
              <div className="seat-aisle-gap" />
              <div className="seat-row-block seat-row-right">
                {finalRightSeats.map((seat) => renderSeatBox(seat))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const getCurrentTimeSlotLabel = () => {
    const slot = availableTimeSlots.find(s => s.slot === selectedTimeSlot);
    if (slot) {
      return `${slot.departureTime || 'N/A'} → ${slot.arrivalTime || 'N/A'}`;
    }
    return 'N/A → N/A';
  };

  const totalSeats = bus.totalSeats || 0;
  const totalAvailableSeats = totalSeats - totalBookedSeats - totalBlockedSeats;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content1 seat-status-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3>Live Seat Status: {bus.busNumber} - {bus.busName}</h3>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">

          <div className="seat-status-controls">
            <div className="control-group">
              <label>
                Date:&nbsp;
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                />
              </label>
            </div>

            <div className="control-group">
              <label>
                Direction:&nbsp;
                <select 
                  value={selectedDirection} 
                  onChange={(e) => setSelectedDirection(e.target.value)}
                  className="direction-select"
                >
                  <option value="UP">UP Journey</option>
                  <option value="DOWN">DOWN Journey</option>
                </select>
              </label>
            </div>

            <div className="control-group">
              <label>
                Time Slot:&nbsp;
                <select 
                  value={selectedTimeSlot} 
                  onChange={(e) => setSelectedTimeSlot(Number(e.target.value))}
                  className="timeslot-select"
                >
                  {availableTimeSlots.map((slot) => (
                    <option key={slot.slot} value={slot.slot}>
                      Slot {slot.slot} - {slot.departureTime || 'N/A'} → {slot.arrivalTime || 'N/A'}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <button className="refresh-btn" onClick={fetchSeatAvailability}>
              Refresh
            </button>
          </div>


          <div className="trip-info">
            <div className="trip-route">
              <span className="direction-label">{selectedDirection} Journey</span>
              <span className="time-slot-label">
                Slot {selectedTimeSlot} - {getCurrentTimeSlotLabel()}
              </span>
            </div>
            <div className="bus-info-summary">
              <span className="bus-name">{bus.busName}</span>
              <span className="bus-number">{bus.busNumber}</span>
              <span className="seat-count">
                Available Seats: {totalAvailableSeats} / {totalSeats}
              </span>
            </div>
          </div>

          {loading && <p className="loading-text">Loading seat status...</p>}
          {error && <p className="error-text" style={{ color: 'red' }}>{error}</p>}

          {!loading && !error && (
            <>
              <div className="instant-badges-row">
                <div className="instant-ticket-badge">
                  Instant Tickets (UP): <strong>{instantCounts.UP || 0}</strong>
                </div>
                <div className="instant-ticket-badge">
                  Instant Tickets (DOWN): <strong>{instantCounts.DOWN || 0}</strong>
                </div>
                <div className="instant-ticket-badge">
                  Total Instant: <strong>{instantCounts.total || 0}</strong>
                </div>
                <div className="instant-ticket-badge">
                  Total Seats Booked: <strong>{totalBookedSeats}</strong>
                </div>
                <div className="instant-ticket-badge blocked-badge">
                  Blocked: <strong>{totalBlockedSeats}</strong>
                </div>
              </div>

              <div className="seat-legend">
                <span><span className="legend-swatch seat-available"></span> Available</span>
                <span><span className="legend-swatch seat-booked"></span> Booked</span>
                <span><span className="legend-swatch seat-blocked"></span> Blocked</span>
              </div>

              <div className="seat-grid-wrapper">
                <div className="seat-grid-column">
                  <h4 style={{ textAlign: 'center' }}>
                    {selectedDirection} Direction - Slot {selectedTimeSlot}
                  </h4>
                  {renderSeatGrid()}
                </div>
              </div>


              {(bus.driver || bus.conductor) && (
                <div className="staff-info">
                  {bus.driver && (
                    <div className="staff-item">
                      <span className="staff-label">Driver:</span>
                      <span className="staff-name">{bus.driver.name || 'N/A'}</span>
                    </div>
                  )}
                  {bus.conductor && (
                    <div className="staff-item">
                      <span className="staff-label">Conductor:</span>
                      <span className="staff-name">{bus.conductor.name || 'N/A'}</span>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="modal-close-btn secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default LiveSeatStatusModal;